/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */
import React, { useState } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import wizardStyle from "@/src/components/Form/wizardStyle";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import { useForm } from "@/src/provider/Form/FormProvider";
import FormSubmitProps from "@/src/types/FormSubmitProps";

import { Box, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import { UpdateResourceParams } from "@/src/services/reactQuery/reactQueryService";
import { UseMutationResult } from "@tanstack/react-query";
import { requiredValidator } from "@/src/services/validationRules";
import Group from "@/src/components/Wizard/Group";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";

type RefineEmploymentLawProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  updateMutation: UseMutationResult<any, unknown, UpdateResourceParams>;
};

export default function RefineEmploymentLaw({ submitProps, product, updateMutation }: RefineEmploymentLawProps) {
  const { handleSubmit, values } = useForm();
  const [isDisabled, setIsDisabled] = useState(false);

  const handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDisabled(true);
    updateMutation.mutateAsync({
      uri: "/pending_cases",
      id: product.id,
      data: { aiRefinementQuestions: null, refinementAnswers: null, initialClientDescriptionAndObjectives: null },
    });
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ergänzende Informationen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Ergänzende Informationen</strong>
              </FormLegend>
            </Grid>

            <Grid size={12}>
              <Group
                type="radio"
                name="clientIsCivilServant"
                question={
                  "Wir haben erkannt, dass es sich bei Ihrem Anliegen um ein arbeitsrechtliches Thema handelt. Für die korrekte Bearbeitung benötigen wir nur noch folgende Information: Befinden Sie sich in einem Beamtenverhältnis?"
                }
                options={[
                  {
                    labelText: "Ja, ich bin verbeamtet",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                  {
                    labelText: "Nein, ich bin angestellt / Sonstiges",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} disabled={isDisabled || !values.clientIsCivilServant} onBackClick={handleBackClick} />
        </Box>
      </FormFader>
    </Paper>
  );
}
